import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaBoxes, FaChevronDown, FaGift, FaLightbulb, FaShoppingCart, FaThLarge } from "react-icons/fa";
import "./BundlesGuide.css";

const STORAGE_KEY = "admin_bundles_guide_open";

const FEATURES = [
  {
    icon: <FaBoxes />,
    title: "Bundles",
    kind: "You create",
    where: "Product page",
    text: "Pick 2 or more products and a discount. Each product in the bundle shows a \"Bundle and save\" box. The discount is taken off at checkout once all of them are in the cart.",
    example: "Shirt Rs. 2,500 + Trousers Rs. 2,500, 10% off: the shopper pays Rs. 4,500.",
  },
  {
    icon: <FaThLarge />,
    title: "Frequently bought together",
    kind: "Automatic",
    where: "Product page",
    text: "Suggests products that customers ordered together. New stores with few orders see products from the same category and best sellers instead.",
    manage: { label: "Show, hide or rename it in the theme editor (Product page)", to: "/admin/theme-editor" },
  },
  {
    icon: <FaShoppingCart />,
    title: "Cart suggestions",
    kind: "Automatic",
    where: "Side cart and cart page",
    text: "\"You may also like\" under the cart items: bundle partners first, then products bought together, then similar products.",
    manage: { label: "Turn it on or off in the theme editor (Side cart)", to: "/admin/theme-editor" },
  },
  {
    icon: <FaGift />,
    title: "Post-purchase offer",
    kind: "You turn on",
    where: "Order confirmation page",
    text: "Appears on the \"Thank you\" page the moment a cash on delivery order is placed, not by email or SMS. The shopper can add one discounted product to the same order with one click. It is collected on delivery.",
    manage: { label: "Set it up at the bottom of this page", anchor: "post-purchase-settings" },
  },
];

const STEPS = [
  { title: "Create a bundle", text: "Choose the products, how many of each, and the discount (percent or a fixed amount). Dates are optional." },
  { title: "Shoppers add it", text: "On the product page they pick sizes or colors in the bundle box and press \"Add bundle to cart\"." },
  { title: "Discount at checkout", text: "Cart and checkout show \"Bundle savings\" automatically. Remove an item and the discount goes away." },
];

const TIPS = [
  "Clear older stock by bundling it with a best seller.",
  "For products with sizes or colors, \"Any option\" lets shoppers choose. Pick one option to discount only that one.",
  "Use start and end dates for sales such as Eid or 11.11. The bundle turns itself on and off.",
  "A bundle stays hidden while any of its products is out of stock or not on the store.",
];

/**
 * Plain-language guide at the top of the Bundles page. Open on the first
 * visit, then remembers whether the admin closed it.
 */
export default function BundlesGuide() {
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) !== "closed";
    } catch {
      return true;
    }
  });

  const toggle = () => {
    setOpen((current) => {
      try {
        localStorage.setItem(STORAGE_KEY, current ? "closed" : "open");
      } catch {
        // ignore
      }
      return !current;
    });
  };

  const scrollTo = (id) => {
    const target = document.getElementById(id);
    if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className={`bg-guide ${open ? "is-open" : ""}`} aria-label="How bundles and offers work">
      <button type="button" className="bg-guide-toggle" onClick={toggle} aria-expanded={open}>
        <span className="bg-guide-toggle-icon"><FaLightbulb size={14} /></span>
        <span className="bg-guide-toggle-text">
          <strong>How bundles and offers work</strong>
          <span>Four ways to get shoppers to buy more in each order</span>
        </span>
        <FaChevronDown className="bg-guide-chevron" size={12} />
      </button>

      {open && (
        <div className="bg-guide-body">
          <div className="bg-features">
            {FEATURES.map((feature) => (
              <div className="bg-feature" key={feature.title}>
                <div className="bg-feature-top">
                  <span className="bg-feature-icon">{feature.icon}</span>
                  <span className={`bg-kind ${feature.kind === "Automatic" ? "is-auto" : ""}`}>{feature.kind}</span>
                </div>
                <h6>{feature.title}</h6>
                <div className="bg-where">Shows on: {feature.where}</div>
                <p>{feature.text}</p>
                {feature.example ? <div className="bg-example">{feature.example}</div> : null}
                {feature.manage?.to ? <Link className="bg-manage" to={feature.manage.to}>{feature.manage.label} →</Link> : null}
                {feature.manage?.anchor ? (
                  <button type="button" className="bg-manage" onClick={() => scrollTo(feature.manage.anchor)}>{feature.manage.label} ↓</button>
                ) : null}
              </div>
            ))}
          </div>

          <div className="bg-lower">
            <div>
              <div className="bg-label">How a bundle works</div>
              <ol className="bg-steps">
                {STEPS.map((step, index) => (
                  <li key={step.title}>
                    <span className="bg-step-no">{index + 1}</span>
                    <div>
                      <strong>{step.title}</strong>
                      <p>{step.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            <div>
              <div className="bg-label">Tips</div>
              <ul className="bg-tips">
                {TIPS.map((tip) => <li key={tip}>{tip}</li>)}
              </ul>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
